import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import {
  CloseCircleOutline as CloseCircle,
  ExclamationCircleOutline as ExclamationCircle,
  useToast,
} from '@aslanpay/portal-common-lib';

import useTerminateIndividual from 'app/hooks/useTerminateIndividual';

import { RemoveEmployeeTerminationDateModalProps } from './types';

function RemoveEmployeeTerminationDateModal({
  employee,
  isOpen,
  onClose,
  onRemove,
}: RemoveEmployeeTerminationDateModalProps) {
  const queryClient = useQueryClient();

  const toast = useToast();

  const { mutate: removeEmployeeTerminationDate, isLoading: isRemovingEmployeeTerminationDate } =
    useTerminateIndividual({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Leave date removed',
          description: `The employee ${employee.firstName} ${employee.lastName} no longer has a leave date`,
        });

        queryClient.invalidateQueries({ queryKey: ['fetchIndividual', employee.id] });

        onRemove();
        onClose();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, the employee leave date was not removed. Please try again.',
        });
      },
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <ExclamationCircle color="system.warning" boxSize="24px" />
            <Text as="h6">Remove leave date?</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircle boxSize="30px" />}
              aria-label="Close remove employee leave date"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text color="brand.mutedPurple" textStyle="body">
            You are about to remove the leave date for the employee{' '}
            <Text as="span" textStyle="h6SemiBold">
              {`${employee.firstName} ${employee.lastName}.`}
            </Text>{' '}
            Do you wish to proceed?
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            isLoading={isRemovingEmployeeTerminationDate}
            onClick={() => removeEmployeeTerminationDate({ id: employee.id, terminationDate: '' })}
          >
            Yes, remove
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RemoveEmployeeTerminationDateModal;
